import { ChefHat, Clock3, Lock, ShieldCheck } from "lucide-react";
import { ChapterTag, FadeUp } from "@/components/Reveal";

const PROPS = [
  { icon: ShieldCheck, title: "FSSAI-grade hygiene", copy: "Clinical cleanliness standards, audited for healthcare environments." },
  { icon: Clock3, title: "24/7 readiness", copy: "Hospitals never close — neither do our canteens." },
  { icon: ChefHat, title: "Nutritionist-approved menus", copy: "Recovery meals, doctor lounges and visitor cafeterias." },
  { icon: Lock, title: "Long-term B2B lock-in", copy: "Contractual revenue that doesn't depend on footfall or apps." },
];

export default function HospitalChapter() {
  return (
    <section id="canteen" data-testid="hospital-chapter" className="relative overflow-hidden bg-obsidian px-5 py-24 sm:px-8 lg:py-32">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-8 top-6 select-none font-display text-[16rem] font-bold leading-none text-outline-light sm:text-[24rem]"
      >
        09
      </span>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 45% 40% at 15% 85%, rgba(16,185,129,0.08) 0%, rgba(11,12,16,0) 70%)" }}
      />

      <div className="relative mx-auto max-w-7xl">
        <ChapterTag index="03" label="Hospital Canteens" />

        <div className="mt-12 grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeUp>
              <h2 className="font-display text-2xl font-semibold leading-[1.12] tracking-tight text-cream sm:text-3xl lg:text-4xl" data-testid="hospital-headline">
                Nine hospitals.{" "}
                <span className="font-serif italic font-medium text-leaf">One trusted kitchen.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.12}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
                While others chase footfall, we signed contracts. Taste Ride operates the canteens
                of nine onboarded hospitals in Ranchi — feeding patients, doctors, staff and
                attendants around the clock. It's revenue that shows up every single day,
                rain or shine.
              </p>
            </FadeUp>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="hospital-value-props">
              {PROPS.map((p, i) => (
                <FadeUp key={p.title} delay={0.15 + i * 0.08}>
                  <div className="group h-full rounded-2xl border border-white/10 bg-coal p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-leaf/40">
                    <p.icon size={22} className="text-leaf" strokeWidth={1.8} />
                    <h3 className="mt-4 font-display text-base font-semibold text-cream">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.copy}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>

          <div className="relative lg:col-span-6">
            <FadeUp delay={0.2}>
              <div className="relative">
                <div className="overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_120px_rgba(16,185,129,0.12)]">
                  <img
                    src="/images/canteen.jpg"
                    alt="Institutional kitchen service line"
                    className="aspect-[4/3] w-full object-cover"
                    data-testid="hospital-image-main"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
                </div>
                <div className="glass-chip absolute -bottom-8 right-4 w-48 overflow-hidden rounded-2xl p-1.5 sm:-right-6 sm:w-64">
                  <img
                    src="/images/kitchen.jpg"
                    alt="Commercial kitchen at work"
                    className="aspect-[16/10] w-full rounded-xl object-cover"
                    data-testid="hospital-image-overlay"
                  />
                </div>
                <div className="mr-44 mt-12 flex items-center gap-3 sm:mr-72 sm:mt-10" data-testid="hospital-caption">
                  <span className="h-2 w-2 rounded-full bg-leaf" />
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                    Recurring daily revenue · High-barrier B2B moat
                  </p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}
